import { t } from './i18n';
import { expandAggregateDays, isMediaAggregate, mediaByDay } from './mediaAggregates';

const DEFAULT_OPTIONS = {
  checkinBurstThreshold: 5,
  routeBurstThreshold: 3,
  longWorkoutMinutes: 45,
  longWorkoutDistanceKm: 8,
  diaryStreakThreshold: 3,
  musicPeakThreshold: 20,
  entertainmentBingeThreshold: 3,
  githubPeakThreshold: 5,
  maxBadgesPerDay: 3,
};

export function getTimelineHighlightDefinitions() {
  return [
    {
      key: 'city_debut',
      label: t('web.hl.city_debut.label'),
      shortLabel: t('web.hl.city_debut.label'),
      priority: 100,
      criteria: t('web.hl.city_debut.criteria'),
    },
    {
      key: 'concert_day',
      label: t('web.hl.concert_day.label'),
      shortLabel: t('web.hl.concert_day.short'),
      priority: 95,
      criteria: t('web.hl.concert_day.criteria'),
    },
    {
      key: 'long_workout',
      label: t('web.hl.long_workout.label'),
      shortLabel: t('web.hl.long_workout.short'),
      priority: 90,
      criteria: t('web.hl.long_workout.criteria'),
    },
    {
      key: 'music_peak',
      label: t('web.hl.music_peak.label'),
      shortLabel: t('label.music'),
      priority: 80,
      criteria: t('web.hl.music_peak.criteria'),
    },
    {
      key: 'github_peak',
      label: t('web.hl.github_peak.label'),
      shortLabel: t('label.code'),
      priority: 75,
      criteria: t('web.hl.github_peak.criteria'),
    },
    {
      key: 'checkin_burst',
      label: t('web.hl.checkin_burst.label'),
      shortLabel: t('web.hl.checkin_burst.short'),
      priority: 70,
      criteria: t('web.hl.checkin_burst.criteria'),
    },
    {
      key: 'route_burst',
      label: t('web.hl.route_burst.label'),
      shortLabel: t('web.hl.route_burst.short'),
      priority: 65,
      criteria: t('web.hl.route_burst.criteria'),
    },
    {
      key: 'diary_streak',
      label: t('web.hl.diary_streak.label'),
      shortLabel: t('label.diary'),
      priority: 60,
      criteria: t('web.hl.diary_streak.criteria'),
    },
    {
      key: 'entertainment_binge',
      label: t('web.hl.entertainment_binge.label'),
      shortLabel: t('web.hl.entertainment_binge.label'),
      priority: 55,
      criteria: t('web.hl.entertainment_binge.criteria'),
    },
  ];
}

/** @deprecated Prefer getTimelineHighlightDefinitions() for locale-aware labels. */
export const TIMELINE_HIGHLIGHT_DEFINITIONS = getTimelineHighlightDefinitions();

const HIGHLIGHT_BY_KEY = TIMELINE_HIGHLIGHT_DEFINITIONS.reduce((acc, item) => {
  acc[item.key] = item;
  return acc;
}, {});

const toDayString = (value) => {
  if (!value) return null;
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  return String(value).slice(0, 10) || null;
};

const routeDay = (route) => {
  const firstPoint = (Array.isArray(route) ? route[0] : route) || {};
  return toDayString(firstPoint.route_date || firstPoint.date);
};

const routeMetric = (route, field) => {
  const firstPoint = (Array.isArray(route) ? route[0] : route) || {};
  const value = Number(firstPoint[field]);
  return Number.isFinite(value) ? value : 0;
};

const increment = (map, day, amount = 1) => {
  if (!day) return;
  map.set(day, (map.get(day) || 0) + amount);
};

const append = (map, day, value) => {
  if (!day) return;
  if (!map.has(day)) map.set(day, []);
  map.get(day).push(value);
};

const createHighlight = (key, date, extra = {}) => ({
  id: `${key}:${date}`,
  date,
  key,
  ...HIGHLIGHT_BY_KEY[key],
  ...extra,
});

const sortAndLimitHighlights = (items, maxBadgesPerDay) => (
  [...items]
    .sort((a, b) => b.priority - a.priority || a.key.localeCompare(b.key) || a.reason.localeCompare(b.reason))
    .slice(0, maxBadgesPerDay)
);

const buildDiaryStreaks = (entries = [], threshold = DEFAULT_OPTIONS.diaryStreakThreshold) => {
  const uniqueDays = [...new Set((entries || []).map((entry) => toDayString(entry.entry_date || entry.date)).filter(Boolean))].sort();
  const streaks = new Map();

  let runStart = 0;
  const flushRun = (endIndex) => {
    const run = uniqueDays.slice(runStart, endIndex);
    if (run.length < threshold) return;
    run.forEach((day, index) => {
      streaks.set(day, index + 1);
    });
  };

  for (let index = 1; index <= uniqueDays.length; index += 1) {
    const previousDay = uniqueDays[index - 1];
    const currentDay = uniqueDays[index];
    const previous = previousDay ? new Date(`${previousDay}T00:00:00Z`) : null;
    const current = currentDay ? new Date(`${currentDay}T00:00:00Z`) : null;
    const isConsecutive = previous && current && ((current - previous) / 86400000) === 1;

    if (!isConsecutive) {
      flushRun(index);
      runStart = index;
    }
  }

  return streaks;
};

const extractMusicCounts = (data = {}, calendarEvents = []) => {
  const musicCounts = new Map();

  const addFromSource = (source) => {
    if (isMediaAggregate(source)) {
      (mediaByDay(source) || []).forEach((row) => {
        increment(musicCounts, toDayString(row.date), Number(row.count) || 0);
      });
      return;
    }
    (source || []).forEach((item) => {
      increment(musicCounts, toDayString(item.played_at || item.date), 1);
    });
  };

  addFromSource(data.spotify);
  addFromSource(data.appleMusic);

  if (musicCounts.size > 0) return musicCounts;

  (calendarEvents || []).forEach((event) => {
    const type = event?.resource?.type;
    if (type !== 'spotify' && type !== 'apple_music') return;
    increment(musicCounts, toDayString(event.start), Number(event?.resource?.data?.count) || 1);
  });

  return musicCounts;
};

export const buildTimelineHighlights = ({ data = {}, calendarEvents = [], githubDays = [] } = {}, options = {}) => {
  const config = { ...DEFAULT_OPTIONS, ...(options || {}) };
  const candidateHighlightsByDate = new Map();
  const checkinCounts = new Map();
  const concertCounts = new Map();
  const routeCounts = new Map();
  const entertainmentCounts = new Map();
  const musicCounts = extractMusicCounts(data, calendarEvents);
  const longWorkoutMetrics = new Map();
  const firstSeenCities = new Map();

  (data.checkins || []).forEach((item) => {
    const day = toDayString(item.date);
    increment(checkinCounts, day, 1);

    const city = String(item.city || '').trim();
    if (city && day) {
      const cityKey = city.toLowerCase();
      const existing = firstSeenCities.get(cityKey);
      if (!existing || day < existing.day) firstSeenCities.set(cityKey, { city, day });
    }
  });

  (data.concerts || []).forEach((item) => {
    const day = toDayString(item.event_date || item.date);
    increment(concertCounts, day, 1);

    const city = String(item.city_name || '').trim();
    if (city && day) {
      const cityKey = city.toLowerCase();
      const existing = firstSeenCities.get(cityKey);
      if (!existing || day < existing.day) firstSeenCities.set(cityKey, { city, day });
    }
  });

  [...(data.health || []), ...(data.moves || []), ...(data.googleMaps || [])].forEach((route) => {
    increment(routeCounts, routeDay(route), 1);
  });

  (data.health || []).forEach((route) => {
    const day = routeDay(route);
    const durationMinutes = routeMetric(route, 'duration') / 60;
    const distanceKm = routeMetric(route, 'distance') / 1000;
    if (!day) return;
    const current = longWorkoutMetrics.get(day) || { durationMinutes: 0, distanceKm: 0 };
    longWorkoutMetrics.set(day, {
      durationMinutes: Math.max(current.durationMinutes, durationMinutes),
      distanceKm: Math.max(current.distanceKm, distanceKm),
    });
  });

  [...expandAggregateDays(data.movies, 'watched_at'),
    ...expandAggregateDays(data.shows, 'watched_at')].forEach((item) => {
    increment(entertainmentCounts, toDayString(item.watched_at || item.date), 1);
  });

  firstSeenCities.forEach(({ city, day }) => {
    append(candidateHighlightsByDate, day, createHighlight('city_debut', day, {
      reason: `Primeiro registro em ${city}.`,
      value: city,
    }));
  });

  concertCounts.forEach((count, day) => {
    append(candidateHighlightsByDate, day, createHighlight('concert_day', day, {
      reason: count === 1 ? t('web.hl.reason.concertOne') : t('web.hl.reason.concertMany', { count }),
      count,
    }));
  });

  longWorkoutMetrics.forEach(({ durationMinutes, distanceKm }, day) => {
    if (durationMinutes < config.longWorkoutMinutes && distanceKm < config.longWorkoutDistanceKm) return;
    append(candidateHighlightsByDate, day, createHighlight('long_workout', day, {
      reason: `${Math.round(durationMinutes)} min e ${distanceKm.toFixed(1)} km de treino.`,
      durationMinutes,
      distanceKm,
    }));
  });

  musicCounts.forEach((count, day) => {
    if (count < config.musicPeakThreshold) return;
    append(candidateHighlightsByDate, day, createHighlight('music_peak', day, {
      reason: t('web.hl.reason.plays', { count }),
      count,
    }));
  });

  (githubDays || []).forEach((item) => {
    const day = toDayString(item.date || item.day);
    const count = Number(item.count) || 0;
    if (!day || count < config.githubPeakThreshold) return;
    append(candidateHighlightsByDate, day, createHighlight('github_peak', day, {
      reason: t('web.hl.reason.github', { count }),
      count,
    }));
  });

  checkinCounts.forEach((count, day) => {
    if (count < config.checkinBurstThreshold) return;
    append(candidateHighlightsByDate, day, createHighlight('checkin_burst', day, {
      reason: `${count} check-ins no mesmo dia.`,
      count,
    }));
  });

  routeCounts.forEach((count, day) => {
    if (count < config.routeBurstThreshold) return;
    append(candidateHighlightsByDate, day, createHighlight('route_burst', day, {
      reason: `${count} rotas/trechos registrados no dia.`,
      count,
    }));
  });

  buildDiaryStreaks(data.diary || [], config.diaryStreakThreshold).forEach((streakLength, day) => {
    append(candidateHighlightsByDate, day, createHighlight('diary_streak', day, {
      reason: t('web.hl.reason.diaryStreak', { count: streakLength }),
      streakLength,
    }));
  });

  entertainmentCounts.forEach((count, day) => {
    if (count < config.entertainmentBingeThreshold) return;
    append(candidateHighlightsByDate, day, createHighlight('entertainment_binge', day, {
      reason: `${count} itens de entretenimento no dia.`,
      count,
    }));
  });

  const byDate = {};
  [...candidateHighlightsByDate.keys()].sort().forEach((day) => {
    byDate[day] = sortAndLimitHighlights(candidateHighlightsByDate.get(day), config.maxBadgesPerDay);
  });

  return {
    rules: TIMELINE_HIGHLIGHT_DEFINITIONS,
    byDate,
    dates: Object.keys(byDate),
  };
};

